import React from 'react'
import { Link } from 'react-router-dom'
import { TruncatedValue } from '../../../common/TruncatedValue'
import { BODY, BORDER, CLOSE_TONE, INK, MONO, MUTED, SOFT } from '../core/consts'
import { TicketCardHeader } from '../core/response.types'
import { formatMskDateTime } from '../../matching/routes/Dashboard/core/mskTime'
import { depositStatusLabel } from '../../matching/routes/DepositPayoutTimeline/core/consts'
import { DISPUTE_STATUS_BADGE, DISPUTE_STATUS_LABEL } from '../../matching/routes/Disputes/core/consts'

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className='d-flex align-items-baseline' style={{ gap: 12 }}>
    <div style={{ width: 132, flexShrink: 0, fontSize: 12.5, color: MUTED }}>{label}</div>
    <div style={{ flexGrow: 1, minWidth: 0 }}>{children}</div>
  </div>
)

/** Время сервера разбирается и форматируется только через единую точку, всегда по МСК. */
const formatDate = (value: string | null): string => (value ? formatMskDateTime(value) : '—')

/**
 * The client is shown the way every other surface shows it: one identifier — the merchant's own —
 * and a link built only from the token. `ClientCell` itself renders a `<td>`, so the card reuses its
 * rule rather than its markup.
 */
const TicketFacts: React.FC<{ header: TicketCardHeader }> = ({ header }) => (
  <>
    <div className='card'>
      <div
        className='d-flex align-items-center'
        style={{ minHeight: 56, padding: '0 24px', borderBottom: `1px solid ${BORDER}` }}
      >
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: INK }}>Данные тикета</h2>
      </div>
      <div className='d-flex flex-column' style={{ padding: '18px 24px', gap: 13 }}>
        <Row label='Токен'>
          <span style={{ fontFamily: MONO, fontSize: 12, color: SOFT }}>{header.token}</span>
        </Row>
        <Row label='Статус'>
          <span className='badge badge-light-primary'>{depositStatusLabel(header.status)}</span>
        </Row>
        {/* Спор показываем там же, где статус: по карточке оператор решает, можно ли вообще
            трогать тикет, а замороженный спором тикет выглядел как обычный. */}
        {header.disputeStatus !== null && (
          <Row label='Спор'>
            <span className={`badge ${DISPUTE_STATUS_BADGE[header.disputeStatus] ?? 'badge-light'}`}>
              {DISPUTE_STATUS_LABEL[header.disputeStatus] ?? header.disputeStatus}
            </span>
          </Row>
        )}
        <Row label='Сумма'>
          <span style={{ fontSize: 15, fontWeight: 600, color: INK }}>
            {header.amount === null
              ? '—'
              : `${header.amount.toLocaleString('ru-RU')} ${header.currency}`}
          </span>
        </Row>

        <div style={{ height: 1, background: BORDER }} />

        <Row label='Мерчант'>
          <span style={{ fontSize: 13, fontWeight: 500, color: BODY }}>{header.merchant ?? '—'}</span>
        </Row>
        <Row label='Транзакция мерчанта'>
          {/* Длину задаёт мерчант — сырым такое значение не выводим. */}
          {header.merchantTransactionId ? (
            <span style={{ fontFamily: MONO, fontSize: 12, color: SOFT }}>
              <TruncatedValue value={header.merchantTransactionId} />
            </span>
          ) : (
            '—'
          )}
        </Row>
        <Row label='Клиент'>
          {header.externalUserId ? (
            <span className='badge'>
              {header.clientToken ? (
                <Link
                  to={`/client/info/${header.clientToken}`}
                  className='text-primary'
                  target='_blank'
                >
                  <TruncatedValue value={header.externalUserId} focusable={false} />
                </Link>
              ) : (
                <TruncatedValue value={header.externalUserId} />
              )}
            </span>
          ) : (
            '—'
          )}
        </Row>
        <Row label='Платёжный метод'>
          <span style={{ fontSize: 13, fontWeight: 500, color: BODY }}>
            {header.paymentMethod ?? '—'}
          </span>
        </Row>

        <div style={{ height: 1, background: BORDER }} />

        <Row label='Создан'>
          <span style={{ fontSize: 13, color: BODY }}>{formatDate(header.createdAt)}</span>
        </Row>
        <Row label='Закрыт'>
          <span style={{ fontSize: 13, color: BODY }}>{formatDate(header.completionDate)}</span>
        </Row>
      </div>
    </div>

    {header.closeReasonTitle && (
      <div className='card' style={{ marginTop: 18 }}>
        <div
          className='d-flex align-items-center'
          style={{ minHeight: 56, padding: '0 24px', borderBottom: `1px solid ${BORDER}` }}
        >
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: INK }}>Причина закрытия</h2>
        </div>
        <div className='d-flex flex-column' style={{ padding: '18px 24px', gap: 10 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: INK }}>{header.closeReasonTitle}</div>
          {header.closeReason && (
            /* Цвет задаёт исход, а не сам факт закрытия: CONFIRMED_BY_PROVIDER — успех, и красная
               плашка на нём читалась как авария. */
            <div
              className='align-self-start'
              style={{
                fontFamily: MONO,
                fontSize: 11.5,
                color: CLOSE_TONE(header.closeReasonSuccessful).chip,
                background: CLOSE_TONE(header.closeReasonSuccessful).bg,
                padding: '6px 10px',
                borderRadius: 6,
              }}
            >
              {header.closeReason}
            </div>
          )}
        </div>
      </div>
    )}
  </>
)

export default TicketFacts
